import { isAxiosError } from "helpers/utils.helper"

// Message fragments that indicate the project number is ABSENT (vs. some other project-number error
// such as "already exists" or a format error, which must keep flowing to the normal error toast).
const MISSING_KEYWORDS = [
	"required",
	"missing",
	"blank",
	"empty",
	"cannot be null",
	"may not be null",
	"this field may not be",
	"no project number",
]

/**
 * Detects the backend's "missing project number" rejection when creating a quotation (REQ-377).
 *
 * Deliberately narrow: it only fires when the `project_number` field carries a required/missing/blank
 * message. Any other 4xx (including other project-number errors like "already exists") returns false so
 * the caller still surfaces the real message via `useApiErrorFormatter`.
 *
 * TODO(REQ-377): lock this to the exact error contract once the create endpoint defines it — the
 * field key + keyword heuristic is a stand-in until then.
 */
export const isMissingProjectNumberError = (e: unknown): boolean => {
	if (!isAxiosError(e)) return false

	const status = e.response?.status
	if (!status || status < 400 || status >= 500) return false

	const data = e.response?.data
	if (!data || typeof data !== "object" || Array.isArray(data)) return false

	const fieldErrors = (data as Record<string, unknown>)["project_number"]
	if (fieldErrors === undefined) return false

	const messages = Array.isArray(fieldErrors) ? fieldErrors : [fieldErrors]
	return messages.some(
		(message) =>
			typeof message === "string" && MISSING_KEYWORDS.some((keyword) => message.toLowerCase().includes(keyword)),
	)
}
